// apps/app/src/routes/Setups/List/state/index.ts

import type { Setup } from '@hvf/contracts'
import { userId } from '@stateApp'
import { createInfiniteQuery, useQueryClient } from '@tanstack/solid-query'
import { createEffect, createSignal, onCleanup } from 'solid-js'
import { useService } from 'solid-services'
import { useFilters } from './filter'
import { fetchSetupsRouter } from './resource/fetchSetupRouter'

const COMPACT_COLUMNS_QUERY = '(max-width: 1535px)'

function useCompactColumns() {
  const mql = typeof window !== 'undefined' ? window.matchMedia(COMPACT_COLUMNS_QUERY) : null
  const [isCompact, setIsCompact] = createSignal(mql?.matches ?? false)

  if (mql) {
    const onChange = (e: MediaQueryListEvent) => setIsCompact(e.matches)
    mql.addEventListener('change', onChange)
    onCleanup(() => mql.removeEventListener('change', onChange))
  }

  return isCompact
}

export enum ListTypes {
  published = 'published',
  mine = 'mine',
  subscribed = 'subscribed',
  taken = 'taken',
  draft = 'draft',
}

export type Pagination = { limit: number; offset: number }

export type ListLayout = 'noramlised' | 'image'

export type ViewMode = 'card' | 'list'
export type CardColumns = 3 | 4 | 5

export type EnrichedSetup = Setup & {
  takers_count?: number
  subscribers_count?: number
  community_score?: number
  commander_score?: number
}

export function useList() {
  const queryClient = useQueryClient()
  const { fetchFilters } = useService(useFilters)()
  const [activeLayout, setActveLayout] = createSignal<ListLayout>('image')
  const [listType, setListType] = createSignal(ListTypes.published)
  const [viewMode, setViewMode] = createSignal<ViewMode>('card')
  const compactColumns = useCompactColumns()
  const [cardColumns, setCardColumns] = createSignal<CardColumns>(compactColumns() ? 3 : 4)

  createEffect(() => {
    if (compactColumns() && cardColumns() > 3) setCardColumns(3)
  })

  const queryKey = () => ['setups', listType(), fetchFilters(), userId()]

  const query = createInfiniteQuery(() => ({
    queryKey: queryKey(),
    queryFn: ({ pageParam = 0 }) => fetchSetupsRouter({
      listType: listType(),
      filters: fetchFilters(),
      pagination: { limit: 10, offset: pageParam },
      userId: userId()
    }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (lastPage.length < 10) return undefined
      return lastPageParam + 10
    }
  }))

  const list = () => (query.data?.pages.flat() as unknown as EnrichedSetup[]) || []
  const hasMore = () => query.hasNextPage
  const isLoading = () => query.isFetching || query.isLoading

  const loadMore = () => {
    if (query.hasNextPage && !query.isFetching) {
      query.fetchNextPage()
    }
  }

  const refetchCurrent = () => {
    query.refetch()
  }

  // The query outlives the List page, so returning to the page doesn't refetch it by itself
  const refetchIfInvalidated = () => {
    if (queryClient.getQueryState(queryKey())?.isInvalidated) {
      query.refetch()
    }
  }

  // Map the infinite query to the shape DataGuard expects,
  // flattening the data so the empty array check functions correctly.
  const listQuery = {
    get isPending() { return query.isPending },
    get isError() { return query.isError },
    get error() { return query.error },
    get data() { return list() },
    refetch: () => query.refetch()
  }

  return {
    activeLayout,
    hasMore,
    isLoading,
    list,
    listType,
    listQuery, // <-- Export the proxy object here
    setActveLayout,
    setListType,
    loadMore,
    refetchCurrent,
    refetchIfInvalidated,
    viewMode,
    setViewMode,
    cardColumns,
    setCardColumns,
    compactColumns,
  }
}
